<script setup>
import ItemThumbnail from "@/Components/ItemThumbnail.vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import TruncatedLineText from "@/Components/TruncatedLineText.vue";
import {useRoute, useRouter} from "vue-router";
import urlSafeTitle from "@/Helpers/UrlTitleHelper";

const emit = defineEmits(['remove-npc']);
const router = useRouter();
const route = useRoute();

const props = defineProps({
    npcs: {
        type: Object,
        default: null,
        nullable: true,
    },
    pagination: {
        type: Object,
        default: null,
        nullable: true,
    },
    displayViewAllButton: {
        type: Boolean,
        default: false,
    },
    displayRemoveButton: {
        type: Boolean,
        default: false,
    },
});

function parseUrl(url) {
    const decodedUrl = decodeURIComponent(url);
    const urlObj = new URL(decodedUrl);
    const { search, pathname } = urlObj;

    const urlParams = new URLSearchParams(search);
    const pathSegments = pathname.split('/');

    return {
        id: pathSegments[pathSegments.length - 1],
        rangeStart: urlParams.get('range_start'),
        resultLimit: urlParams.get('result_limit'),
        isHistory: pathSegments.includes('history'),
    };
}

function routeFromUrl(url) {
    const { id, rangeStart, resultLimit, isHistory } = parseUrl(url);
    const currentTitle = route.query.title || null;
    if (isHistory) {
        router.push({ name: 'merchant-history', query: { range_start: rangeStart, result_limit: resultLimit} })
    } else {
        router.push({ name: 'booklet', query: { id: id, range_start: rangeStart, result_limit: resultLimit, title: urlSafeTitle(currentTitle)} })
    }
}

function npcLink(npc) {
    const npcId = npc.id;
    const ncpName = npc.name;
    return router.resolve({ name: 'merchant', query: { npc_id: npcId, name: urlSafeTitle(ncpName)}} ).href;
}

function routeToMerchantHistory() {
    router.push({ name: 'merchant-history', query: { range_start: 0, result_limit: 10} })
}

function truncateName(name) {
    if (name.length > 16) {
        return name.substring(0, 16) + '...';
    }
    return name;
}

function removeNpc(npcId) {
    emit('remove-npc', npcId);
}

</script>

<template>
    <div class="overflow-hidden bg-gray-800 shadow sm:rounded-md">
        <ul role="list" class="divide-y divide-gray-500">
            <li v-for="npc in props.npcs" :key="npc.id">
                <router-link :to="npcLink(npc)" class="block hover:bg-slate-700 cursor-pointer" >
                    <div class="flex items-center px-4 py-4 sm:px-6">
                        <div class="flex min-w-0 flex-1 items-center relative">
                            <ItemThumbnail v-if="npc.id"
                                :image-url="`${npc.illustrations[0]?.url}?resolution=256x256`"
                                :image-description="npc.illustrations[0]?.image_description"
                                :is-zoomable="true"
                                :height="'8rem'"
                                :width="'8rem'"
                            />
                            <div class="flex-1 px-4 sm:gap-4 gap-4 flex items-center h-24 overflow-hidden">
                                <div class="">
                                    <p style="min-width: 10rem" class="w-full min-[420px]:w-26 sm:w-26 md:w-26 text-ellipsis text-base font-bold text-gray-100">{{ truncateName(npc.name) }}</p>
                                    <p class="text-base font-medium text-gray-100">{{ npc.race }}</p>
                                    <p class="text-base font-medium text-gray-100">{{ npc.class }}</p>
                                </div>
                                <div class="overflow-hidden min-[420px]:block hidden" style="margin-left: auto">
                                    <TruncatedLineText class="mt-2 flex items-center text-sm text-gray-200 overflow-hidden">
                                        {{ npc.backstory }}
                                    </TruncatedLineText>
                                </div>
                                <div class="items-end md:hidden" style="margin-left: auto;">
                                    <p class="items-end mt-2 flex items-center text-sm text-gray-200">
                                        <FontAwesomeIcon icon="fa-solid fa-chevron-right" />
                                    </p>
                                </div>
                            </div>
                            <div v-if="props.displayRemoveButton"  class="absolute md:bottom-5 top-0 right-0">
                                <FontAwesomeIcon class="cursor-pointer ml-2" :icon="['far', 'trash-xmark']" style="color: #dc2626;" @click.stop="removeNpc(npc.id)"/>
                            </div>
                        </div>
                    </div>
                </router-link>
            </li>
        </ul>
        <nav v-if="props.pagination != null && (props.pagination.previous_url || props.pagination.next_url)" class="mt-2 mb-2 flex items-center justify-between px-4 py-3 sm:px-6" aria-label="Pagination">
            <div class="hidden sm:block">
                <p class="text-sm text-gray-200">
                    Showing
                    <span class="font-medium">{{props.pagination.range_start}}</span>
                    to
                    <span class="font-medium">{{props.pagination.range_end}}</span>
                    of
                    <span class="font-medium">{{props.pagination.total_items}}</span>
                    results
                </p>
            </div>
            <div class="flex flex-1 justify-between sm:justify-end">
                <PrimaryButton :disabled="!props.pagination.previous_url" class="mr-2" @click="routeFromUrl(props.pagination.previous_url)">Previous</PrimaryButton>
                <PrimaryButton :disabled="!props.pagination.next_url" class="ml-2" @click="routeFromUrl(props.pagination.next_url)">Next</PrimaryButton>
            </div>
        </nav>
        <nav v-if="props.displayViewAllButton === true" class="border-t border-gray-500 flex items-center justify-between px-4 py-2 sm:px-6" aria-label="View All">
            <div class="flex flex-1 justify-end ">
                <PrimaryButton class="mr-2" @click="routeToMerchantHistory">View History</PrimaryButton>
            </div>
        </nav>
    </div>
</template>

<style scoped>

</style>
